import type { ProductLine } from "../../config/products";

export type { ProductLine };

export type BillingPeriod = "weekly" | "monthly" | "quarterly" | "yearly" | "one_time";
export const BILLING_PERIODS: BillingPeriod[] = ["monthly", "yearly", "quarterly", "weekly", "one_time"];
export const CURRENCIES = ["inr", "usd", "eur", "gbp", "aed", "sar", "cad", "aud", "sgd", "jpy"] as const;

export type PlanCategory = "STARTER" | "GROWTH" | "ADDON" | "ENTERPRISE" | "FREE" | "CREATOR";
export type Visibility = "PUBLIC" | "PRIVATE";
export type AccountType = "ALL" | "CLIENT" | "AGENCY";

export interface AdminPrice {
  id: number;
  planId: number;
  stripePriceId: string;
  billingPeriod: BillingPeriod;
  amount: string;
  currency: string;
  trialDays: number | null;
  creditAmount: number | null;
  isActive: boolean;
  liveSubscribers: number;
}

export interface PlanConfigRow {
  key: string;
  value: string;
  valueType: string;
  label: string;
  description: string | null;
  displayOrder: number;
  isVisible: boolean;
}

export interface AdminPlan {
  id: number;
  name: string;
  description: string | null;
  isActive: boolean;
  accountType: AccountType;
  category: PlanCategory;
  productLine: ProductLine;
  visibility: Visibility;
  basePlanId: number | null;
  variantKey: string | null;
  prices: AdminPrice[];
  drawpost_plan_configs: PlanConfigRow[];
}

export interface PlanAssignment {
  id: number;
  userId: number;
  planId: number;
  isActive: boolean;
  assignedBy: number | null;
  note: string | null;
  createdAt: string;
  plans?: { id: number; name: string; productLine: ProductLine; visibility: Visibility; isActive: boolean };
}

export interface Coupon {
  id: number;
  code: string | null;
  description: string | null;
  discountPercent: string;
  duration: "FIRST_MONTH" | "FOREVER";
  autoApply: boolean;
  maxRedemptions: number | null;
  timesRedeemed: number;
  perUserLimit: number;
  firstPaidUpgradeOnly: boolean;
  startsAt: string | null;
  expiresAt: string | null;
  isActive: boolean;
  razorpayOfferId: string | null;
  razorpayOfferIdCard: string | null;
  razorpayOfferIdUpi: string | null;
  createdAt: string;
}

export type OfferScope = "GLOBAL" | "TARGETED";
export type OfferAudience = "NON_PAID" | "NEVER_PAID";
export const OFFER_NUDGE_KEYS = [
  "FIRST_POST",
  "LOW_CREDITS",
  "CREDITS_EXHAUSTED",
  "PRICING_INTENT",
  "CHECKOUT_ABANDONED",
  "RETURNING_USER",
  "REPEAT_USAGE",
] as const;
export type OfferNudgeKey = (typeof OFFER_NUDGE_KEYS)[number];

export const NUDGE_LABEL: Record<OfferNudgeKey, string> = {
  FIRST_POST: "First post generated",
  LOW_CREDITS: "Credits running low",
  CREDITS_EXHAUSTED: "Out of credits",
  PRICING_INTENT: "Returns to pricing",
  CHECKOUT_ABANDONED: "Abandoned checkout",
  RETURNING_USER: "Returning after 7+ days",
  REPEAT_USAGE: "Several posts on Free",
};

export interface OfferDisplay {
  badge?: string;
  title?: string;
  subtitle?: string;
  ctaLabel?: string;
  imageUrl?: string;
}

export interface OfferItem {
  id?: number;
  basePlanId: number;
  variantKey: string;
  badge: string | null;
  sortOrder: number;
}

export interface PlanOffer {
  id: number;
  slug: string;
  name: string;
  scope: OfferScope;
  audience: OfferAudience;
  nudgeKey: OfferNudgeKey | null;
  startsAt: string | null;
  endsAt: string | null;
  windowHours: number | null;
  maxRedemptions: number | null;
  maxGrantsPerDay: number | null;
  display: OfferDisplay | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  plan_offer_items: OfferItem[];
  _count?: { user_offer_grants: number };
}

export interface CampaignOffer {
  id: number;
  name: string;
  description: string | null;
  enrollUntil: string;
  freeDays: number;
  isActive: boolean;
  createdAt: string;
}

export interface UserSummary {
  id: number;
  email: string;
  firstName: string | null;
  lastName: string | null;
}

export interface CreditLedgerItem {
  id: string;
  entryType: string;
  bucket: string;
  amount: number;
  source: string;
  expiresAt: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export interface UserCredits {
  summary: { available: number | null; granted: number; spent: number; expired: number; adjusted: number };
  items: CreditLedgerItem[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

/** Global weekly/monthly limits per brand for each content type; null = no limit. */
export type LimitedContentType = "still_image" | "carousel" | "video" | "story" | "blog_post";
export type ContentLimitSettings = Record<LimitedContentType, { weekly: number | null; monthly: number | null }>;
/** GET/PUT response: the limits plus the highest value allowed per period. */
export interface ContentLimitAdminView {
  limits: ContentLimitSettings;
  max: { weekly: number; monthly: number };
}
